import { useState } from "react";
import { useQueries, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { LabFields } from "@/components/NewBatchDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, AlertTriangle, PackageMinus, PackagePlus } from "lucide-react";
import { toast } from "sonner";

const Items = ({ items, back, testid }) => (
  <div className="space-y-1" data-testid={testid}>
    {items.map((it) => (
      <div key={`${it.material_id}-${it.fused}-${it.length || ""}`} className="flex items-center justify-between gap-3 text-sm">
        <span>{it.name} <span className="text-muted-foreground">{[it.color, it.supplier].filter(Boolean).join(" · ")}</span></span>
        {back ? <span className="font-medium text-emerald-400">+{num(it.qty)} {it.unit}</span> : (
          <span className={`text-right font-medium ${it.sufficient ? "" : "text-rose-400"}`}>
            -{num(it.needed)} {it.unit}
            <span className="block text-[11px] font-normal text-muted-foreground">
              in magazzino {num(it.available)} {it.unit}{!it.sufficient && ` · mancano ${num(it.needed - it.available)}`}
            </span>
          </span>
        )}
      </div>
    ))}
  </div>
);

const PHASE_ORDER = ["programmato", "taglio", "confezione", "controllo", "completato"];
const idx = (p) => Math.max(0, PHASE_ORDER.indexOf(p));

// Conferma del cambio fase per tutti i colori della scheda taglio, con i materiali scalati o rimessi per colore
export const PhaseMoveDialog = ({ batches, phase, label, back, onClose }) => {
  const qc = useQueryClient();
  const lots = batches.filter((b) => (back ? idx(b.phase) > idx(phase) : idx(b.phase) < idx(phase)));
  const first = lots[0] || batches[0];
  const needLab = !back && phase === "confezione";
  const [lab, setLab] = useState(first.lab || "");
  const [date, setDate] = useState(first.expected_delivery || "");
  const { data: labs = [] } = useQuery({ queryKey: ["labs"], enabled: needLab, queryFn: async () => (await api.get("/production/labs")).data });
  const previews = useQueries({
    queries: lots.map((b) => ({
      queryKey: ["stock-preview", b.id, b.phase, phase],
      queryFn: async () => (await api.get(`/production/${b.id}/stock-preview`, { params: { phase } })).data,
      staleTime: 0, gcTime: 0,
    })),
  });
  const isLoading = previews.some((p) => p.isLoading);
  const go = useMutation({
    mutationFn: async () => {
      for (const b of lots) await api.patch(`/production/${b.id}`, { phase, ...(needLab ? { lab, expected_delivery: date || null } : {}) });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["production"] });
      qc.invalidateQueries({ queryKey: ["materials"] });
      qc.invalidateQueries({ queryKey: ["labs-summary"] });
      toast.success(back ? `Lotto riportato in ${label}` : `Lotto passato in ${label}`);
      onClose();
    },
    onError: (e) => { qc.invalidateQueries({ queryKey: ["production"] }); toast.error(e.response?.data?.detail || "Errore"); },
  });
  const all = previews.map((p) => p.data).filter(Boolean);
  const short = all.some((d) => d.discharge.some((s) => s.items.some((i) => !i.sufficient)));
  const empty = !isLoading && all.every((d) => !d.discharge.length && !d.restore.length);
  const total = lots.reduce((t, b) => t + (b.quantity || 0), 0);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="phase-move-dialog">
        <DialogHeader>
          <DialogTitle>{back ? "Riporta a" : "Passa a"} {label}</DialogTitle>
          <DialogDescription>{first.product_name} · {lots.map((b) => b.color || "Senza colore").join(", ")} · {num(total)} pz</DialogDescription>
        </DialogHeader>
        {isLoading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : (
          <div className="space-y-4">
            {lots.map((b, n) => { const data = previews[n].data; return (data.discharge.length > 0 || data.restore.length > 0) && (
              <div key={b.id} className="space-y-2" data-testid={`phase-move-color-${b.color || b.batch_number}`}>
                <p className="text-sm font-semibold uppercase tracking-wide">{b.color || "Senza colore"} <span className="font-normal normal-case text-muted-foreground">· {num(b.quantity)} pz</span></p>
            {data.discharge.map((s) => (
              <div key={s.stage} className="rounded-md border border-border/60 p-3">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary"><PackageMinus className="h-3.5 w-3.5" />{s.label} scalati dal magazzino</p>
                {s.items.length ? <Items items={s.items} testid={`phase-move-${s.stage}${n ? `-${n}` : ""}`} /> : <p className="text-sm text-muted-foreground">Niente da scalare.</p>}
                {s.notes.map((n) => <p key={n} className="mt-2 text-[11px] text-amber-400">{n}</p>)}
              </div>
            ))}
            {data.restore.map((s) => (
              <div key={s.stage} className="rounded-md border border-emerald-500/30 p-3">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-400"><PackagePlus className="h-3.5 w-3.5" />{s.label} rimessi in magazzino</p>
                <Items items={s.items} back testid={`phase-restore-${s.stage}${n ? `-${n}` : ""}`} />
              </div>
            ))}
              </div>
            ); })}
            {short && <p className="flex items-start gap-1.5 rounded bg-rose-500/10 px-2 py-1.5 text-xs text-rose-400"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />Materiale non sufficiente: la giacenza scende a zero. Ordina quello che manca.</p>}
            {empty && <p className="text-sm text-muted-foreground">In questo passaggio non si scala nessun materiale.</p>}
            {needLab && (
              <div className="grid grid-cols-2 gap-3 rounded-md border border-primary/40 p-3" data-testid="phase-move-lab-section">
                <p className="col-span-2 text-xs font-semibold uppercase tracking-wide text-primary">A chi lo consegni per la confezione (obbligatorio)</p>
                <LabFields lab={lab} date={date} onLab={setLab} onDate={setDate} labs={labs} testid="phase-move" />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="phase-move-cancel">Annulla</Button>
          <Button onClick={() => go.mutate()} disabled={isLoading || go.isPending || (needLab && !lab)} data-testid="phase-move-confirm">
            {go.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Conferma e {back ? "riporta a" : "passa a"} {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const StockMoves = ({ batch }) => {
  const moves = Object.entries(batch.stock_moves || {});
  if (!moves.length) return null;
  return (
    <div className="mt-3 space-y-1 rounded-md bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-400" data-testid={`stock-moves-${batch.batch_number}`}>
      {moves.map(([s, m]) => (
        <p key={s}>{s === "taglio" ? "Tessuto scalato" : "Accessori scalati"}: {m.items.map((i) => `${i.name} ${i.color || ""} ${num(i.qty)} ${i.unit}`).join(" · ")}</p>
      ))}
    </div>
  );
};
